import type { Mutate, StateCreator, StoreApi, UseBoundStore } from "zustand";
import type { DevtoolsOptions, PersistOptions } from "zustand/middleware";

import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createWithEqualityFn } from "zustand/traditional";

import { isDevtoolsEnabled } from "./devtools";
import { initImmer } from "./immer-setup";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DevtoolsConfig {
    /** Store identifier in Redux DevTools (groups actions under this key). */
    store: string;
    /** Namespace prefix in Redux DevTools (e.g. "workspace/sdk"). */
    name: string;
    /**
     * Whether the app is running in production. Controls the default devtools
     * state (OFF in prod, ON otherwise) before the `state_devtools` cookie
     * override is checked.
     *
     * Supplied by the consuming app so this package has no dependency on Vite
     * or any bundler. Typically: `import.meta.env.VITE_ENV_TYPE === "production"`.
     */
    isProd: boolean;
    /**
     * Extra options forwarded to the Zustand devtools middleware.
     * Use for store-specific knobs such as `serialize: { options: { map: true } }`.
     */
    extra?: Omit<DevtoolsOptions, "enabled" | "store" | "name"> & Record<string, unknown>;
}

export interface StoreOptions<T> {
    /**
     * Devtools configuration.
     * - `{ store, name, isProd }`: devtools enabled with explicit Redux DevTools
     *   grouping. Enabled/disabled based on `isProd` and the `state_devtools`
     *   cookie override. See {@link isDevtoolsEnabled}.
     * - `false` or omitted: skip devtools entirely (no middleware wrapping).
     */
    devtools?: DevtoolsConfig | false;

    /**
     * Zustand `persist` middleware options. When provided, persistence is enabled.
     * Full stack order is defined in {@link composeStore} (immer first when using
     * {@link createImmerStore}, then persist, optional custom `middleware`, then devtools).
     *
     * In practice most stores use `partialize` and behave like `Partial<T>`, but the second
     * `PersistOptions` generic is `any` so callsites can pair custom `storage` / `merge` typing
     * with a dedicated persisted type (see chat/session stores).
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PersistedState varies per store; see JSDoc above
    persist?: PersistOptions<T, any>;

    /**
     * Default equality function for `useStore(selector)` calls.
     * When provided, uses `createWithEqualityFn` instead of `create`.
     * Typically pass `shallow` from `"zustand/shallow"`.
     */
    equalityFn?: <U>(a: U, b: U) => boolean;

    /**
     * Enable `subscribeWithSelector` middleware (outermost in the chain).
     * Adds `.subscribe(selector, listener, options)` overload to the store.
     */
    subscribeWithSelector?: boolean;

    /**
     * Escape hatch for middleware not covered by the factory options.
     *
     * Receives the composed `StateCreator` **after** immer and persist have been
     * applied, but **before** devtools and subscribeWithSelector. This is the
     * most useful insertion point — your custom middleware sees the final data
     * shape but runs before observability layers.
     *
     * The callback must return a `StateCreator` (i.e., wrap the input with your
     * middleware and return the result).
     *
     * @example
     * ```ts
     * import { redux } from "zustand/middleware";
     *
     * const useStore = createSimpleStore<State>({
     *     devtools: { store: "my-store", name: "namespace", isProd },
     *     middleware: (creator) => someCustomMiddleware(creator, { option: true }),
     * })((set) => ({ ... }));
     * ```
     */
    middleware?: (creator: StateCreator<T, any, any>) => StateCreator<T, any, any>;
}

/**
 * Return type for factory-created stores. Includes the `subscribeWithSelector`
 * overload (enhanced `.subscribe(selector, listener, options)`) and the
 * `persist` API (`.persist.rehydrate()`, `.persist.hasHydrated()`, etc.).
 *
 * Both mutators are always present in the type even if not used at runtime.
 * This is safe because:
 * - Without `subscribeWithSelector`, the 3-arg subscribe overload simply isn't
 *   called, and the 1-arg overload still works.
 * - Without `persist`, the `.persist` property won't exist at runtime, but
 *   callers that don't access it are unaffected.
 */
type StoreResult<T> = UseBoundStore<
    Mutate<StoreApi<T>, [["zustand/persist", T], ["zustand/subscribeWithSelector", never]]>
>;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Builds the devtools middleware config from caller options.
 *
 * - `false` / `undefined` → devtools skipped entirely (no middleware wrapping)
 * - `{ store, name, isProd }` → devtools enabled per {@link isDevtoolsEnabled}
 *
 * Zustand ≥5.0.5 automatically infers action names from the calling function
 * via stack trace when no explicit name is passed to `set()`. Explicit names
 * always take priority.
 */
function resolveDevtoolsConfig(opts?: DevtoolsConfig | false): DevtoolsOptions | false {
    if (!opts) return false;

    const enabled = isDevtoolsEnabled(opts.isProd);
    return { enabled, store: opts.store, name: opts.name, ...opts.extra };
}

/**
 * Composes middleware around an initializer in canonical order and creates the store.
 *
 * ## Canonical ordering (outermost → innermost)
 *
 * ```
 * subscribeWithSelector? → devtools? → [custom middleware]? → persist? → immer? → storeCreator
 * ```
 *
 * Source: Zustand's own `middlewareTypes.test.tsx` documents common nestings such as
 * `devtools(persist(immer(...)))`. This factory adds `subscribeWithSelector` **outside**
 * devtools when enabled, so selectors see the same state DevTools records.
 * Custom middleware (via `options.middleware`) runs between persist and devtools.
 *
 * ## Adding a new built-in middleware option
 *
 * 1. Add a field to {@link StoreOptions} with JSDoc explaining its purpose.
 * 2. Add an `if` block in this function at the correct position in the chain.
 *    - Innermost = closest to the store creator (data transforms: immer, persist)
 *    - Outermost = closest to the consumer (observability: devtools, subscribeWithSelector)
 * 3. If the middleware augments the store API (like `persist` adds `.persist` or
 *    `subscribeWithSelector` adds the selector overload), update {@link StoreResult}
 *    to include its mutator in the `Mutate` type chain.
 * 4. Add a test in `create-store.test.ts` that exercises the new option.
 *
 * Type assertions (`as any`) are used internally because TypeScript cannot
 * infer middleware composition through conditional runtime branches. The
 * runtime chain is always correct.
 */

interface ComposeStoreParams<T> {
    initializer: StateCreator<T, any, any>;
    devToolsConfig: DevtoolsOptions | false;
    options?: StoreOptions<T>;
    useImmer?: boolean;
}

function composeStore<T>({ initializer, devToolsConfig, options, useImmer }: ComposeStoreParams<T>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- middleware generics require runtime composition; see JSDoc above
    let composed: any = initializer;

    if (useImmer) {
        composed = immer(composed);
    }

    if (options?.persist) {
        composed = persist(composed, options.persist as any);
    }

    if (options?.middleware) {
        composed = options.middleware(composed);
    }

    if (devToolsConfig !== false) {
        composed = devtools(composed, devToolsConfig);
    }

    if (options?.subscribeWithSelector) {
        composed = subscribeWithSelector(composed);
    }

    if (options?.equalityFn) {
        return createWithEqualityFn<T>()(composed, options.equalityFn) as any;
    }
    return create<T>()(composed) as any;
}

// ---------------------------------------------------------------------------
// Factory: createSimpleStore
// ---------------------------------------------------------------------------

/**
 * Create a Zustand store for flat/shallow state updates. No Immer overhead.
 *
 * Middleware applied in canonical order based on options provided:
 * `subscribeWithSelector? → devtools? → persist? → storeCreator`
 *
 * Use this when the store only does top-level property assignments
 * (e.g. `set({ agentId: id })`). If you later need nested mutations,
 * Map/Set, or array operations, switch to {@link createImmerStore}.
 *
 * @example
 * ```ts
 * // Simple store with devtools
 * const useAgentStore = createSimpleStore<AgentState>({
 *     devtools: { store: "agent", name: "workspace/sdk", isProd },
 * })((set) => ({
 *     agentId: "---",
 *     setAgentId: (id) => set({ agentId: id }, false, "setAgentId"),
 * }));
 *
 * // With persistence
 * const useLanguageStore = createSimpleStore<LanguageState>({
 *     devtools: { store: "language", name: "workspace/sales-tools", isProd },
 *     persist: { name: "language-store" },
 * })((set) => ({
 *     language: "en-US",
 *     setLanguage: (lang) => set({ language: lang }),
 * }));
 *
 * // With equalityFn (replaces createWithEqualityFn)
 * const useStore = createSimpleStore<State>({
 *     equalityFn: shallow,
 *     persist: { name: "my-store", skipHydration: true },
 * })((set) => ({ ... }));
 * ```
 */
export function createSimpleStore<T>(options?: StoreOptions<T>) {
    const devToolsConfig = resolveDevtoolsConfig(options?.devtools);

    return (initializer: StateCreator<T, [["zustand/devtools", never]], []>): StoreResult<T> => {
        return composeStore({ initializer: initializer as StateCreator<T, any, any>, devToolsConfig, options });
    };
}

// ---------------------------------------------------------------------------
// Factory: createImmerStore
// ---------------------------------------------------------------------------

/**
 * Create a Zustand store with Immer draft mutations, Map/Set support, and optimized array methods.
 *
 * Middleware applied in canonical order based on options provided:
 * `subscribeWithSelector? → devtools? → [custom middleware]? → persist? → immer → storeCreator`
 *
 * - With `subscribeWithSelector: true`, that middleware is **outermost** (wraps devtools).
 * - `immer` is **innermost** (wraps the store creator and provides the draft).
 *
 * **Immer plugin enablement:** {@link initImmer} is called on every invocation (same
 * helper as the canary tests in `immer-setup.test.ts`). This is safe and effectively
 * free because Immer's `loadPlugin` deduplicates — later calls are cheap.
 * See: https://github.com/immerjs/immer/blob/master/src/utils/plugins.ts
 *
 * @example
 * ```ts
 * // Simple immer store
 * const useDragStore = createImmerStore<DragState>({
 *     devtools: { store: "drag", name: "drag-store", isProd },
 * })((set) => ({
 *     items: [],
 *     addItem: (item) => set((state) => { state.items.push(item); }),
 * }));
 *
 * // With persistence and Map serialization
 * const useChatStore = createImmerStore<ChatState>({
 *     devtools: { store: "chat", name: "workspace/chat", isProd, extra: { serialize: { options: { map: true } } } },
 *     persist: { name: "chat-store", partialize: (s) => ({ messages: s.messages }), storage: customStorage },
 * })((set, get) => ({
 *     messages: new Map(),
 *     addMessage: (msg) => set((state) => { state.messages.set(msg.id, msg); }),
 * }));
 *
 * // With subscribeWithSelector
 * const useGaiaStore = createImmerStore<GaiaState>({
 *     devtools: { store: "gaia", name: "expert/gaia", isProd },
 *     subscribeWithSelector: true,
 * })((set) => ({ ... }));
 *
 * // With equalityFn (replaces createWithEqualityFn + immer)
 * const useStore = createImmerStore<State>({
 *     equalityFn: shallow,
 *     persist: { name: "my-store", skipHydration: true },
 *     devtools: { store: "my-store", name: "workspace/domain", isProd },
 * })((set) => ({ ... }));
 * ```
 */
export function createImmerStore<T>(options?: StoreOptions<T>) {
    initImmer();

    const devToolsConfig = resolveDevtoolsConfig(options?.devtools);

    return (
        initializer: StateCreator<T, [["zustand/devtools", never], ["zustand/immer", never]], []>,
    ): StoreResult<T> => {
        return composeStore({
            initializer: initializer as StateCreator<T, any, any>,
            devToolsConfig,
            options,
            useImmer: true,
        });
    };
}
